import {
  useState, useMemo 
} from 'react';
import {
  Gavel, Clock, CheckCircle, DollarSign, AlertTriangle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  useBids,
  BidCard,
  BidDetailModal,
  type Bid,
  type BidStatus,
  BID_STATUS_LABELS,
} from '@/features/procurement';
import { toast } from 'sonner';

interface StatusTab {
  value: BidStatus | undefined;
  label: string;
}

const STATUS_TABS: StatusTab[] = [
  {
    value: undefined,
    label: 'All' 
  },
  {
    value: 'submitted',
    label: 'Submitted' 
  },
  {
    value: 'under_review',
    label: 'Under Review' 
  },
  {
    value: 'accepted',
    label: 'Accepted' 
  },
  {
    value: 'rejected',
    label: 'Rejected' 
  },
];

export function BidsView() {
  const [selectedStatus, setSelectedStatus] = useState<BidStatus | undefined>(undefined);
  const [selectedBid, setSelectedBid] = useState<Bid | null>(null);

  const {
    data, isLoading, error, refetch 
  } = useBids({ status: selectedStatus });
  const { data: allData } = useBids();

  const bids = data?.data ?? [];

  const stats = useMemo(() => {
    const allBids = allData?.data ?? [];
    return {
      total: allBids.length,
      pending: allBids.filter((b) => b.status === 'submitted' || b.status === 'under_review').length,
      accepted: allBids.filter((b) => b.status === 'accepted').length,
      totalValue: allBids.reduce((sum, b) => sum + b.total, 0),
    };
  }, [allData]);

  const handleAccept: (bidId: string) => void = () => {
    toast.success('Bid accepted');
    setSelectedBid(null);
    refetch();
  };

  const handleReject: (bidId: string) => void = () => {
    toast.success('Bid rejected');
    setSelectedBid(null);
    refetch();
  };

  const handleCreatePO: (bidId: string) => void = () => {
    toast.success('Purchase order created from bid');
    setSelectedBid(null);
    refetch();
  };

  const handleCardClick = (bid: Bid) => {
    setSelectedBid(bid);
  };

  const exportData = bids.map((bid) => ({
    eventNumber: bid.eventNumber,
    eventTitle: bid.eventTitle,
    supplier: bid.supplierName,
    status: BID_STATUS_LABELS[bid.status],
    total: bid.total,
    currency: bid.currency,
    rank: bid.rank ?? '-',
    isLowestBid: bid.isLowestBid ? 'Yes' : 'No',
    submittedAt: bid.submittedAt ?? '-',
  }));

  return (
    <>
      <CrudToolbar
        entityName="Bid"
        entityNamePlural="Bids"
        selectedCount={0}
        exportData={exportData}
        exportFilename="bids.csv"
        canAdd={false}
        canEdit={false}
        canDelete={false}
        canBulkUpload={false}
      />

      {selectedBid && (
        <BidDetailModal
          bid={selectedBid}
          isOpen={true}
          onClose={() => setSelectedBid(null)}
          onAccept={handleAccept}
          onReject={handleReject}
          onCreatePO={handleCreatePO}
        />
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Bids</CardTitle>
            <Gavel className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Review
            </CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Accepted</CardTitle>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.accepted}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
            <DollarSign className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              ${stats.totalValue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            variant={selectedStatus === tab.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedStatus(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`skel-${i.toString()}`} className="h-56 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load bids</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && bids.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Gavel className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">Supplier Bids</p>
            <p className="text-sm text-muted-foreground">Review and evaluate supplier bids</p>
            <div className="mt-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.pending}</p>
              <p className="text-sm text-muted-foreground">Active bids to review</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && bids.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {bids.map((bid) => (
            <div
              key={bid.bidId}
              className="cursor-pointer rounded-lg transition-all hover:ring-2 hover:ring-primary/50"
              onClick={() => handleCardClick(bid)}
            >
              <BidCard bid={bid} onAccept={handleAccept} onReject={handleReject} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
